import { Inject, Injectable } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { ANGULAR_LOCALES, LOCALES } from '@fb-tool-front/shared/constants/locale.constant';
import { AuthService } from './auth.service';
import { catchError, take } from 'rxjs/operators';
import { ErrorService } from './error.service';
import { BehaviorSubject, of } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LOCAL_STORAGE, WINDOW } from '@ng-web-apis/common';
import { localStorageLocale } from '@fb-tool-front/shared/constants/shared.constant';
import { MetricsService, TRACKABLE_EVENTS } from '@fb-tool-front/core/services/metrics.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class LocaleService {
  private _locale: LOCALES;

  locale$: BehaviorSubject<string> = new BehaviorSubject(this.locale);
  localeLoading$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  set locale(value: LOCALES) {
    this._locale = value;
    this.locale$.next(this.locale);
  }

  get locale(): LOCALES {
    return this._locale || LOCALES.en;
  }

  constructor(
    private translateService: TranslateService,
    private authService: AuthService,
    private errorService: ErrorService,
    private metricsService: MetricsService,
    @Inject(LOCAL_STORAGE) readonly localStorage: Storage,
    @Inject(WINDOW) readonly window: Window
  ) {
    this.registerLocale(this.locale);
    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe(() => {
      this.localeLoading$.next(false);
    });

    this.registerLocale(LOCALES.en);
  }

  isEngLocale() {
    return this.locale === LOCALES.en;
  }

  saveLocaleToLocalStorage() {
    this.localStorage.setItem(localStorageLocale, this.locale);
  }

  switchLocale() {
    this.metricsService.trackEvent(
      TRACKABLE_EVENTS.changeLanguage({
        lang: this.locale
      })
    );

    const newLocale = this.locale === LOCALES.en ? LOCALES.ru : LOCALES.en;

    this.saveLocale(newLocale);
    this.registerLocale(newLocale);
  }

  saveLocale(locale: LOCALES) {
    this.authService
      .changeUserData({ locale })
      .pipe(
        take(1),
        catchError(error => {
          this.errorService.captureError(error);

          return of(null);
        })
      )
      .subscribe();
  }

  private registerLocale(locale: LOCALES) {
    this.localeLoading$.next(true);
    this.translateService.setDefaultLang(locale);
    this.translateService.use(locale);
    this.locale = locale;
    registerLocaleData(ANGULAR_LOCALES[locale], locale);

    // angular pipes are pure, so we can't change angular locale at runtime
    // but we don't have any pipes depended on locale right now
    // also we can monkey patch angular pipes like this
    // https://github.com/angular/angular/issues/15039#issuecomment-496133812
  }
}
